import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
} from '@mui/material';
import {
  Api as ApiIcon,
  Storage as StorageIcon,
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  LocationOn as LocationIcon,
  MoreHoriz as ComingSoonIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* 헤더 섹션 */}
        <Box sx={{ pt: 6, pb: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h2" component="h1" fontWeight="bold" color="primary.main">
              Open API
            </Typography>
            <Chip 
              label="BETA" 
              color="primary" 
              size="small"
              sx={{ 
                height: '24px',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                bgcolor: 'primary.main'
              }} 
            />
          </Box>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
            서비스 구현을 위한 다양한 API 솔루션
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
            규랩스의 Open-API로 여러분의 서비스 개발을 더욱 쉽고 효율적으로 만들어보세요.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/console')}
            >
              콘솔로 이동
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/docs')}
            >
              API 문서 보기
            </Button>
          </Box>
        </Box>

        {/* 제공 API 목록 섹션 */}
        <Box sx={{ py: 6 }}>
          <Typography variant="h4" textAlign="center" fontWeight="bold" sx={{ mb: 4 }}>
            제공 API
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SchoolIcon color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h6" fontWeight="bold">
                      전국 초중고 시간표/급식/학사일정
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    전국 모든 초중고의 시간표, 급식, 학사일정 정보를 제공합니다.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocationIcon color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h6" fontWeight="bold">
                      영문 주소 변환
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    국내 주소를 영문으로 변환하여 제공합니다.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ComingSoonIcon color="disabled" sx={{ mr: 2 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Coming Soon
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    더 많은 API가 준비중입니다.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* 요금제 섹션 */}
        <Box sx={{ py: 6 }}>
          <Typography variant="h4" textAlign="center" fontWeight="bold" sx={{ mb: 4 }}>
            요금제
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ height: '100%', position: 'relative' }}>
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Free
                  </Typography>
                  <Chip 
                    label="현재 제공중" 
                    color="success" 
                    size="small" 
                    sx={{ alignSelf: 'flex-start', mb: 2 }}
                  />
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    ₩0
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    하루 1,000토큰 제공
                  </Typography>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    onClick={() => navigate('/console')}
                    sx={{ mt: 'auto' }}
                  >
                    시작하기
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Standard와 Enterprise 카드는 비슷한 구조로 유지 */}
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Standard
                  </Typography>
                  <Chip 
                    label="Beta 기간 후 제공 예정" 
                    color="default" 
                    size="small" 
                    sx={{ alignSelf: 'flex-start', mb: 2 }}
                  />
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    Coming Soon
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Beta 서비스 종료 후 제공될 예정입니다
                  </Typography>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    disabled
                    sx={{ mt: 'auto' }}
                  >
                    준비중
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Enterprise
                  </Typography>
                  <Chip 
                    label="Beta 기간 후 제공 예정" 
                    color="default" 
                    size="small" 
                    sx={{ alignSelf: 'flex-start', mb: 2 }}
                  />
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    Coming Soon
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Beta 서비스 종료 후 제공될 예정입니다
                  </Typography>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    disabled
                    sx={{ mt: 'auto' }}
                  >
                    준비중
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Alert 
            severity="info" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto',
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%',
                textAlign: 'center'
              }
            }}
          >
            추가 토큰이 필요하신 경우 <strong>kyulabs@kyulabs.app</strong>로 문의해 주세요.
          </Alert>
        </Box>

        {/* 주요 기능 섹션 */}
        <Box sx={{ py: 6 }}>
          <Typography variant="h4" textAlign="center" fontWeight="bold" sx={{ mb: 4 }}>
            주요 기능
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <ApiIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    API 명세 제공
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Swagger를 통한 상세한 API 명세와 테스트 환경 제공
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <StorageIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    간편한 공공 API 연동
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    복잡한 공공 API를 쉽고 직관적으로 사용
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    상세한 사용 로그
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    연산시간, 응답시간 및 결과를 포함한 상세 로그 제공
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* 푸터 */}
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} 규랩스 (kyulabs). All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default HomePage;